import { Box, Button, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { PhoneNumberUtil } from 'google-libphonenumber'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

interface WhatsAppEmployeeData {
  phoneNumber?: string | null
  country?: string | null
  fullName: string
}

interface Props {
  employee: WhatsAppEmployeeData
  canSeePersonalInfo: boolean
  hasWhatsappFeature: boolean
}

const COUNTRY_TO_REGIONS: Record<string, string> = {
  uk: 'gb',
}

const formatPhoneNumber = ({
  phoneNumber,
  country,
}: {
  phoneNumber?: string
  country?: string
}): [true, string] | [false, null] => {
  if (!phoneNumber || !country) {
    return [false, null]
  }

  const phoneNumberUtil = PhoneNumberUtil.getInstance()
  try {
    const regionCode = Object.keys(COUNTRY_TO_REGIONS).includes(country)
      ? COUNTRY_TO_REGIONS[country]
      : country

    const cleanedPhoneNumber = phoneNumber.replaceAll(/\D/g, '')
    const phone = phoneNumberUtil.parseAndKeepRawInput(cleanedPhoneNumber, regionCode)
    if (!phoneNumberUtil.isPossibleNumber(phone)) {
      return [false, null]
    }
    return [true, phone.getCountryCode() + '' + phone.getNationalNumber()]
  } catch {
    return [false, null]
  }
}

export const getWhatsappUrl = (phoneData: {
  phoneNumber?: string
  country?: string
}): [true, string] | [false, ''] => {
  const [isPhoneOk, phone] = formatPhoneNumber(phoneData)

  if (!isPhoneOk) return [false, '']

  return [true, 'https://wa.me/' + phone]
}

export const WhatsAppButton = ({ employee, canSeePersonalInfo, hasWhatsappFeature }: Props) => {
  const [open, toggle] = useToggle(false)

  const onClickOpenWhatsappChat = () => {
    track(trackingEvents.comms.openWhatsappChatWithEmployee)
  }

  const [isPhoneOk, whatsappUrl] = getWhatsappUrl({
    phoneNumber: employee.phoneNumber ?? undefined,
    country: employee.country ?? undefined,
  })
  const renderButton = canSeePersonalInfo && hasWhatsappFeature && isPhoneOk

  useEffect(() => {
    renderButton &&
      track(trackingEvents.comms.whatsappChatWithEmployeeShown, { disabled: !isPhoneOk })
  }, [renderButton, isPhoneOk])

  if (!renderButton) {
    return <></>
  }

  const showTooltip = open && !isPhoneOk

  return (
    <Box flexDirection="row" paddingY="s4" paddingX="s4">
      <Tooltip.Alt
        open={showTooltip}
        toggle={toggle}
        attachment="top center"
        label={i18n.t('common.chat_on_whatsapp.phone_not_available', {
          employee_name: employee.fullName,
        })}
        textAlign="center"
      >
        <Button.Router
          blankTarget
          disabled={!isPhoneOk}
          type="secondary"
          to={whatsappUrl}
          onClick={onClickOpenWhatsappChat}
          iconName="whatsapp-chat"
        >
          {i18n.t('common.chat_on_whatsapp.call_to_action')}
        </Button.Router>
      </Tooltip.Alt>
    </Box>
  )
}
